<template>
  <div class="highlight-header-section">
    <div class="highlight-header-section__inner">
      <h3
        class="highlight-header-section__title"
        v-if="!screenLandScape || lockOrientation !== undefined"
      >
        {{ $t('RaceVideoPage.highlights.title') }}
      </h3>

      <div class="highlight-header-section__filter">
        <filter-selector-parts
          class="highlight-header-section__filter__item"
          defaultItemLabel="Driver"
          defaultListItemLabel="Driver (all)"
          :filterList="driverFilterList"
          @filterEnabled="handleEnabledFilterSelect"
          @filterChange="onChangeDriverFilter"
        />
        <filter-selector-parts
          v-show="selectedHighlightTab === 'official'"
          class="highlight-header-section__filter__item"
          defaultItemLabel="Scene"
          defaultListItemLabel="Scene (all)"
          :filterList="sceneFilterList"
          @filterEnabled="handleEnabledFilterSelect"
          @filterChange="onChangeSceneOrUserFilter"
        />
        <filter-selector-parts
          v-show="selectedHighlightTab === 'user'"
          class="highlight-header-section__filter__item"
          defaultItemLabel="User"
          defaultListItemLabel="User (all)"
          :filterList="userFilterList"
          @filterEnabled="handleEnabledFilterSelect"
          @filterChange="onChangeSceneOrUserFilter"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, Ref } from '@vue/composition-api'
import FilterSelectorParts, {
  FilterList,
} from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/FilterSelectorParts.vue'
import StoreUtil from '@/store/StoreUtil'
import { LockOrientationType } from '@/views/RaceVideoPage/RaceVideoPage.vue'
import type {
  HighlightUserFilterType,
  HighlightTabsType,
} from '@/store/stores/pageStore/RaceVideoPage/RaceVideoPageStore'

type DataType = {
  driverFilterId: string
}

/**
 * レース動画再生画面 ハイライト表示 ヘッダーセクションのコンポーネント
 */
export default defineComponent({
  name: 'HighlightHeaderSection',
  components: {
    FilterSelectorParts,
  },
  data(): DataType {
    return {
      driverFilterId: '',
    }
  },
  props: {
    /**
     * 画面向き
     */
    screenOrientationType: {
      type: String,
      default: 'portrait-primary',
    },
    /**
     * 画面固定
     */
    lockOrientation: {
      type: String as PropType<LockOrientationType>,
    },
  },
  setup(props) {
    const screenLandScape = computed(() => props.screenOrientationType?.indexOf('landscape') !== -1)
    const raceVideoPageStore = StoreUtil.useStore('RaceVideoPageStore')

    return {
      selectedHighlightTab: raceVideoPageStore.selectedHighlightTab as Ref<HighlightTabsType>,
      screenLandScape,
      driverFilterList: raceVideoPageStore.participatingPlayers,
      sceneFilterList: raceVideoPageStore.playEventFilterListForOfficialHighlights as Ref<
        Array<FilterList>
      >,
      userFilterList: raceVideoPageStore.userFilterListForUserCreateHighlights as Ref<
        Array<FilterList>
      >,
    }
  },
  methods: {
    /**
     * 選手・シーンフィルタが選択・解除された場合に呼び出される。
     */
    handleEnabledFilterSelect(value: boolean): void {
      this.$emit('filterSelectEnabled', value)
    },
    onChangeDriverFilter(id: string): void {
      this.$emit('onChangeDriverFilter', id)
    },
    onChangeSceneOrUserFilter(id: string, type?: HighlightUserFilterType): void {
      this.$emit('onChangeSceneOrUserFilter', id, type)
    },
  },
})
</script>

<style lang="scss" scoped>
.highlight-header-section {
  width: 100%;
  padding: 0 24px 0 20px;
  background: $black80;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 8%);
  color: $white;

  &__inner {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    max-width: 375px;
    width: 100%;
    height: 44px;
    margin: 0 auto;
  }

  &__title {
    @include fontSetTextMedium();

    letter-spacing: 0.04em;
  }

  &__filter {
    position: relative;
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-end;
    width: min(57.067vw, 214px);
    height: 36px;
    column-gap: 4px;

    &__item {
      flex: 1;
      max-width: calc(50% - 2px);
    }
  }
}
</style>

<style lang="scss">
.highlight-header-section {
  &__filter {
    .filter-selector-parts__list {
      width: clamp(240px, 64vw, 280px);
      max-height: calc(#{var(--screen-height)} - 56.25vw - 44px);
    }
  }
}

.race-video-highlights-pane {
  &--landscape-primary,
  &--landscape-secondary {
    .highlight-header-section {
      height: 44px;
      padding: 0 6px;

      &__title {
        display: none;
      }

      &__filter {
        width: 100%;
      }
    }

    .filter-selector-parts__list {
      --content-height: calc(var(--screen-height) - 44px);

      width: 100%;
      max-height: calc(#{var(--screen-height)} - 44px);
    }
  }
}
</style>
